import {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type CommunityGroupsWidgetProps,
  type CommunityGroupsThemingProps,
  GROUPS_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionCommunityGroups for imperative control.
 */
export interface LegionCommunityGroupsHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildCommunityGroupsWidgetUrl(
  props: CommunityGroupsWidgetProps
): string {
  const {
    baseUrl,
    count = 6,
    layout = "grid",
    theme = "light",
    showViewAll = true,
    communityUrl = "/community",
    viewAllUrl,
    sortBy = "trending",
  } = props;

  const url = new URL("/widget/community-groups", baseUrl);
  url.searchParams.set("count", String(Math.min(Math.max(count, 1), 20)));
  url.searchParams.set("layout", layout);
  url.searchParams.set("theme", theme);
  url.searchParams.set("showViewAll", String(showViewAll));
  url.searchParams.set("communityUrl", communityUrl);
  url.searchParams.set("viewAllUrl", viewAllUrl || `${communityUrl}/groups`);
  url.searchParams.set("sortBy", sortBy);

  // Append theming params
  const themingKeys = Object.keys(GROUPS_THEMING_PROP_TO_PARAM) as Array<
    keyof CommunityGroupsThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(GROUPS_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionCommunityGroups – a React component that embeds the Legion
 * Community Groups widget via an iframe.
 */
export const LegionCommunityGroups = forwardRef<
  LegionCommunityGroupsHandle,
  CommunityGroupsWidgetProps
>(function LegionCommunityGroups(props, ref) {
  const {
    count = 6,
    layout = "grid",
    theme = "light",
    showViewAll,
    communityUrl,
    viewAllUrl,
    sortBy,
    height = "auto",
    width = "100%",
    className,
    style,
    onLoad,
    onError,
    authToken,
    baseUrl,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildCommunityGroupsWidgetUrl(props),
    [
      baseUrl,
      count,
      layout,
      theme,
      showViewAll,
      communityUrl,
      viewAllUrl,
      sortBy,
      ...Object.keys(GROUPS_THEMING_PROP_TO_PARAM).map(
        (k) => rest[k as keyof typeof rest]
      ),
    ]
  );

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (authToken && iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: "AUTH_TOKEN", token: authToken },
        baseUrl
      );
    }
    onLoad?.({
      widget: "community-groups",
      count: Math.min(Math.max(count, 1), 20),
      theme,
    });
  }, [authToken, baseUrl, count, theme, onLoad]);

  const handleError = useCallback(() => {
    onError?.("Failed to load community groups widget");
  }, [onError]);

  const resolvedHeight = height === "auto" ? "200px" : height;

  return (
    <div
      className={className}
      style={{ width, height: resolvedHeight, ...style }}
      data-testid="legion-community-groups"
    >
      <iframe
        ref={iframeRef}
        src={src}
        title="Community Groups Widget"
        width="100%"
        height="100%"
        frameBorder="0"
        scrolling="no"
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
        style={{ border: "none" }}
      />
    </div>
  );
});
